import React, { useState } from 'react';

const TokenDisplay = ({ solBalance, tokens = [], loading = false, error = null }) => {
  const [showAll, setShowAll] = useState(false);
  
  // Format numbers with appropriate precision
  const formatBalance = (balance, decimals = 6) => {
    if (!balance || balance === 0) return '0';
    
    const num = parseFloat(balance);
    if (num < 0.000001) return '< 0.000001';
    if (num < 1) return num.toFixed(6);
    if (num < 1000) return num.toFixed(4);
    if (num < 1000000) return `${(num / 1000).toFixed(2)}K`;
    return `${(num / 1000000).toFixed(2)}M`;
  };

  // Get token symbol from mint address (simplified - in production you'd use a token registry)
  const getTokenSymbol = (mint) => {
    const knownTokens = {
      'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v': 'USDC',
      'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB': 'USDT',
      'So11111111111111111111111111111111111111112': 'WSOL',
      'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263': 'BONK',
      '7vfCXTUXx5WJV5JADk17DUJ4ksgau7utNKj4b963voxs': 'WIF',
      'JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN': 'JUP'
    };
    
    return knownTokens[mint] || `${mint.slice(0, 4)}...${mint.slice(-4)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-2 mt-2">
        <div className="w-3 h-3 border border-gray-300 border-t-purple-600 rounded-full animate-spin"></div>
        <span className="text-xs text-gray-500 dark:text-gray-400">Loading balances...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-2 text-xs text-red-600 dark:text-red-400">
        Failed to load balances
      </div>
    );
  }

  // Sort tokens by balance value and get top 2
  const sortedTokens = [...tokens].sort((a, b) => parseFloat(b.amount) - parseFloat(a.amount));
  const visibleTokens = showAll ? sortedTokens : sortedTokens.slice(0, 2);
  const hasMoreTokens = sortedTokens.length > 2;

  return (
    <div className="mt-2 space-y-1">
      {/* SOL Balance - Always shown */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center">
            <span className="text-xs font-bold text-white">S</span>
          </div>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">SOL</span>
        </div>
        <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">
          {formatBalance(solBalance)}
        </span>
      </div>

      {/* Token Balances */}
      {visibleTokens.map((token, index) => (
        <div key={token.mint} className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
              <span className="text-xs text-gray-600 dark:text-gray-300">T</span>
            </div>
            <span className="text-xs text-gray-600 dark:text-gray-400">
              {getTokenSymbol(token.mint)}
            </span>
          </div>
          <span className="text-xs text-gray-700 dark:text-gray-300">
            {formatBalance(token.amount)}
          </span>
        </div>
      ))}

      {/* Show More/Less Toggle */}
      {hasMoreTokens && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="w-full text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-200 py-1 flex items-center justify-center space-x-1 border-t border-gray-100 dark:border-gray-600 pt-2"
        >
          <span>{showAll ? 'Show Less' : `+${sortedTokens.length - 2} more`}</span>
          <svg 
            className={`w-3 h-3 transition-transform ${showAll ? 'rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m19 9-7 7-7-7" />
          </svg>
        </button>
      )}

      {/* No tokens message */}
      {sortedTokens.length === 0 && solBalance > 0 && (
        <div className="text-xs text-gray-500 dark:text-gray-400 italic py-1">
          SOL only
        </div>
      )}

      {/* Empty wallet message */}
      {sortedTokens.length === 0 && (!solBalance || solBalance === 0) && (
        <div className="text-xs text-gray-400 dark:text-gray-500 italic py-1">
          Empty wallet
        </div>
      )}
    </div>
  );
};

export default TokenDisplay;